<template>
  <div class="mag">
    <div class="mag-gallery" :style="{ gridTemplateColumns: gridTemplateColumnsStyle }">
      <div
        v-for="(item, i) in displayItems"
        :key="item.id + '_' + i"
        class="mag-item"
      >
        <button
          type="button"
          class="mag-thumb"
          data-testid="mag-thumb"
          :style="galleryItemStyle"
          @click="openPopover(item.id)"
        >
          <img
            :src="item.thumbSrc"
            :alt="item.fileName"
            class="mag-thumb-img"
          />
          <span
            v-if="i === 0 && isShowCover"
            class="mag-badge mag-badge--cover"
          >
            {{ coverLabel }}
          </span>
          <span
            v-if="hasRejectedBadge(item.id)"
            class="mag-badge mag-badge--rejected"
          >
            {{ $t('lbl_EMPBE_Rejected') }}
          </span>
        </button>
      </div>
    </div>

    <MediaPreviewPopover
      :visible="popoverOpen"
      :title="title"
      :mode="popoverRejectMode"
      :media-type="mediaType"
      :media="activeMedia"
      :reject-reason="activeRejectReason"
      :rejected="popoverRejected"
      :templates="popoverFrfTemplates"
      :actor="rejectionActor"
      :show-reject-ui="canRejectActiveMedia"
      :show-prev="canGoPrev"
      :show-next="canGoNext"
      @close="closePopover"
      @prev="goPrev"
      @next="goNext"
      @update:rejectReason="onRejectReasonInput"
      @update:rejected="onRejectedUpdate"
    />
  </div>
</template>

<script>
import {
  getMediaUploaderImageDataObj,
  getMediaUploaderVideoDataObj,
} from '@/_helpers/media-uploader-utils.js';
import MediaPreviewPopover from './mediaPreviewPopover.vue';
import { isProductApprovalResultRadioSelected } from '@/pages/NewproductInfoCenter/productApprovalFieldMeta';

function isNonEmptyReason(value) {
  return String(value == null ? '' : value).trim() !== '';
}

export default {
  name: 'MediaApprovalGallery',
  components: {
    MediaPreviewPopover,
  },
  props: {
    MediaArray: { type: Array, default: () => [] },
    mode: {
      type: String,
      default: 'admin',
      validator: (value) => ['admin', 'customer'].includes(value),
    },
    mediaType: {
      type: String,
      default: 'image',
      validator: (value) => ['image', 'video'].includes(value),
    },
    galleryItemStyle: { type: String, default: 'height: 120px; width: 120px;' },
    gridTemplateColumns: { type: String, default: '' },
    isShowCover: { type: Boolean, default: false },
    title: { type: String, default: '' },
    approvalContentResults: { type: Array, default: () => [] },
    approvalContentResultDrafts: { type: Object, default: () => ({}) },
    rejectionTemplates: { type: Array, default: () => [] },
    rejectionActor: { type: String, default: '' },
  },
  data() {
    return {
      popoverOpen: false,
      activeMediaId: null,
      popoverRejected: false,
    };
  },
  computed: {
    isAdmin() {
      return this.mode === 'admin';
    },
    frfTemplates() {
      return this.isAdmin ? this.rejectionTemplates : [];
    },
    /** 仅当前媒体在 Approval_Request_Content 有行时才显示/允许填驳回 */
    canRejectActiveMedia() {
      return !!(this.isAdmin && this.activeResultRow);
    },
    popoverRejectMode() {
      return this.canRejectActiveMedia ? 'admin' : 'customer';
    },
    popoverFrfTemplates() {
      return this.canRejectActiveMedia ? this.frfTemplates : [];
    },
    normalizedMedia() {
      return (this.MediaArray || []).map((item) => {
        if (this.mediaType === 'video') {
          return getMediaUploaderVideoDataObj(item);
        }
        return getMediaUploaderImageDataObj(item);
      });
    },
    displayItems() {
      return this.normalizedMedia.map((media) => ({
        id: media.id,
        fileName: media.fileName || '',
        thumbSrc:
          this.mediaType === 'video'
            ? (media.thumbnail && media.thumbnail.previewSrc) || ''
            : media.previewSrc || '',
      }));
    },
    gridTemplateColumnsStyle() {
      return this.gridTemplateColumns || `repeat(${Math.max(this.displayItems.length, 1)}, 1fr)`;
    },
    coverLabel() {
      return this.mediaType === 'video'
        ? this.$t('lbl_EMPBE_Uploader_MainVideo')
        : this.$t('lbl_EMPBE_UploaderCover');
    },
    activeMedia() {
      if (this.activeMediaId == null) return null;
      return this.normalizedMedia.find((m) => m.id === this.activeMediaId) || null;
    },
    activeIndex() {
      if (this.activeMediaId == null) return -1;
      return this.normalizedMedia.findIndex((m) => m.id === this.activeMediaId);
    },
    canGoPrev() {
      return this.activeIndex > 0;
    },
    canGoNext() {
      return this.activeIndex >= 0 && this.activeIndex < this.normalizedMedia.length - 1;
    },
    activeResultRow() {
      return this.findResultRow(this.activeMediaId);
    },
    activeRejectReason() {
      return this.getCurrentReason(this.activeMediaId);
    },
  },
  methods: {
    findResultRow(mediaId) {
      if (mediaId == null) return null;
      const rows = this.approvalContentResults || [];
      const key = String(mediaId);
      return rows.find((row) => row && String(row.usageId) === key) || null;
    },
    getCurrentReason(mediaId) {
      if (mediaId == null) return '';
      const drafts = this.approvalContentResultDrafts || {};
      if (Object.prototype.hasOwnProperty.call(drafts, mediaId)) {
        return drafts[mediaId] == null ? '' : String(drafts[mediaId]);
      }
      const strKey = String(mediaId);
      if (Object.prototype.hasOwnProperty.call(drafts, strKey)) {
        return drafts[strKey] == null ? '' : String(drafts[strKey]);
      }
      const row = this.findResultRow(mediaId);
      return row && row.rejectReason != null ? String(row.rejectReason) : '';
    },
    /** 审批模式：resultCode 0/2/4 即视为驳回；编辑/供应商只看驳回文案 */
    isMediaRejected(mediaId) {
      const row = this.findResultRow(mediaId);
      if (this.isAdmin) {
        if (!row) return false;
        const code = row.resultCode ?? row.ResultCode ?? '';
        if (isProductApprovalResultRadioSelected(code)) return true;
      }
      return isNonEmptyReason(this.getCurrentReason(mediaId));
    },
    hasRejectedBadge(mediaId) {
      return this.isMediaRejected(mediaId);
    },
    openPopover(mediaId) {
      this.activeMediaId = mediaId;
      this.popoverRejected = this.isMediaRejected(mediaId);
      this.popoverOpen = true;
    },
    closePopover() {
      this.popoverOpen = false;
      this.activeMediaId = null;
      this.popoverRejected = false;
    },
    goPrev() {
      if (!this.canGoPrev) return;
      const prev = this.normalizedMedia[this.activeIndex - 1];
      this.openPopover(prev.id);
    },
    goNext() {
      if (!this.canGoNext) return;
      const next = this.normalizedMedia[this.activeIndex + 1];
      this.openPopover(next.id);
    },
    emitDraftChange({ rejected, rejectReason }) {
      if (!this.isAdmin || this.activeMediaId == null || !this.activeResultRow) return;
      const payload = {
        mediaId: this.activeMediaId,
        rejected,
        rejectReason,
      };
      const row = this.activeResultRow;
      if (row && row.approvalRequestResultId != null) {
        payload.approvalRequestResultId = row.approvalRequestResultId;
      }
      this.$emit('approval-draft-change', payload);
    },
    onRejectReasonInput(value) {
      if (!this.isAdmin) return;
      this.emitDraftChange({
        rejected: this.popoverRejected,
        rejectReason: value == null ? '' : String(value),
      });
    },
    onRejectedUpdate(rejected) {
      if (!this.isAdmin) return;
      this.popoverRejected = !!rejected;
      this.emitDraftChange({
        rejected: this.popoverRejected,
        rejectReason: this.activeRejectReason,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.mag-gallery {
  display: grid;
  gap: 8px;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
}

.mag-item {
  position: relative;
}

.mag-thumb {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #f9f9f9;
  cursor: pointer;
}

.mag-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.mag-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  position: absolute;
  padding: 2px 4px;
  border-radius: 4px;
  color: #fff;
  font-family: 'inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: 0.15px;
  white-space: nowrap;

  &--cover {
    top: 0;
    right: 0;
    background: #2c6ecb;
  }

  &--rejected {
    bottom: 0;
    left: 0;
    background: #dc3545;
  }
}
</style>
